/**
 * NdaWall — full-screen click-wrap NDA gate shown to new beta testers the
 * moment they log in, before they can touch anything else. Signing records
 * name + email + timestamp + IP + NDA version server-side, then drops the
 * tester straight into the tool. Signed copy downloadable as PDF.
 */
import React, { useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import { FileSignature, Check, Download, ArrowRight, Loader, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function NdaWall({ onSigned }) {
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [fullName, setFullName] = useState(user?.name || "");
  const [agreed, setAgreed] = useState(false);
  const [scrolledToEnd, setScrolledToEnd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [signature, setSignature] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/nda/document").then((r) => setDoc(r.data)).catch(() => setDoc(null));
  }, []);

  // If the text fits without scrolling, unlock immediately.
  useEffect(() => {
    if (!doc) return;
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + 8) setScrolledToEnd(true);
  }, [doc]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setScrolledToEnd(true);
  };

  const sign = async () => {
    if (fullName.trim().length < 2) {
      setError("Please type your full legal name.");
      return;
    }
    if (!agreed) {
      setError("Please tick the agreement checkbox.");
      return;
    }
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/nda/sign", { full_name: fullName.trim(), agreed: true });
      setSignature(data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = () => {
    if (!doc || !signature) return;
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 48;
    const contentW = pageW - margin * 2;
    let y = margin;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(14);
    pdf.text(doc.title, margin, y, { maxWidth: contentW });
    y += 28;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);
    const lines = pdf.splitTextToSize(doc.text, contentW);
    for (const line of lines) {
      if (y > pageH - margin - 20) {
        pdf.addPage();
        y = margin;
      }
      pdf.text(line, margin, y);
      y += 13;
    }

    if (y > pageH - margin - 130) {
      pdf.addPage();
      y = margin;
    }
    y += 20;
    pdf.setDrawColor(15, 27, 45);
    pdf.line(margin, y, pageW - margin, y);
    y += 20;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text("ELECTRONICALLY SIGNED", margin, y);
    y += 18;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    const rows = [
      `Signed by: ${signature.full_name}`,
      `Email: ${signature.email}`,
      `Date (UTC): ${signature.signed_at}`,
      `NDA version: ${signature.nda_version}`,
      `Document SHA-256: ${signature.nda_sha256}`,
      `IP address: ${signature.ip}`,
    ];
    rows.forEach((r) => { pdf.text(r, margin, y); y += 15; });

    pdf.save(`resmo-nda-signed-${(signature.full_name || "tester").replace(/\s+/g, "_")}.pdf`);
  };

  return (
    <div className="min-h-screen rm-paper flex items-center justify-center px-4 py-10" data-testid="nda-wall">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-5">
          <div className="inline-flex items-center gap-2 rm-tag mb-3" style={{ background: "#FFC145" }}>
            <FileSignature size={13} /> One quick step before you enter
          </div>
          <h1 className="font-display text-3xl md:text-4xl uppercase" style={{ color: "#0F1B2D" }}>
            {signature ? "You're in." : "Sign the NDA."}
          </h1>
          {!signature && (
            <p className="text-sm mt-2" style={{ color: "#475569" }}>
              Resmo is in private beta. Read the agreement, sign it right here, and you&apos;ll be
              inside in under a minute — no emails, no waiting.
            </p>
          )}
        </div>

        <div className="rm-card-static p-5 md:p-6" style={{ background: "white" }}>
          {signature ? (
            <div className="text-center py-6" data-testid="nda-success">
              <div className="inline-flex items-center justify-center rounded-full mb-4"
                   style={{ width: 64, height: 64, background: "#D1FAE5", border: "3px solid #0E5A4A" }}>
                <Check size={30} style={{ color: "#0E5A4A" }} strokeWidth={3} />
              </div>
              <div className="font-display text-xl uppercase mb-1" style={{ color: "#0F1B2D" }}>
                NDA signed — welcome aboard.
              </div>
              <p className="text-[13px] mb-1" style={{ color: "#475569" }}>
                Signed by <strong>{signature.full_name}</strong> on{" "}
                {new Date(signature.signed_at).toLocaleString()} · version {signature.nda_version}
              </p>
              <p className="font-mono-rm text-[10px] uppercase tracking-widest mb-6" style={{ color: "#94A3B8" }}>
                <ShieldCheck size={11} className="inline mr-1" />
                Audit trail recorded — keep a copy for your records
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button onClick={downloadPdf} className="rm-btn rm-btn-secondary text-sm" data-testid="nda-download-btn">
                  <Download size={15} /> Download signed copy
                </button>
                <button onClick={onSigned} className="rm-btn rm-btn-primary text-sm" data-testid="nda-enter-btn">
                  Enter Resmo <ArrowRight size={15} />
                </button>
              </div>
            </div>
          ) : !doc ? (
            <div className="py-12 text-center font-mono-rm text-[11px] uppercase tracking-widest" style={{ color: "#94A3B8" }}>
              <Loader size={16} className="animate-spin inline mr-2" /> Loading agreement…
            </div>
          ) : (
            <>
              <div className="font-display uppercase text-base mb-2" style={{ color: "#0F1B2D" }}>
                {doc.title}
              </div>
              <div
                ref={scrollRef}
                onScroll={onScroll}
                className="rounded-md p-4 mb-2 text-[12.5px] leading-relaxed whitespace-pre-wrap"
                style={{
                  height: 300, overflowY: "auto",
                  background: "#F8FAFC", border: "1.5px solid #CBD5E1", color: "#334155",
                }}
                data-testid="nda-text"
              >
                {doc.text}
              </div>
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-4"
                   style={{ color: scrolledToEnd ? "#0E5A4A" : "#B45309", fontWeight: 700 }}
                   data-testid="nda-scroll-hint">
                {scrolledToEnd ? "· Read to the end — you can sign now" : "· Scroll to the end of the agreement to unlock signing"}
              </div>

              <label className="block mb-3">
                <div className="font-mono-rm text-[9px] uppercase tracking-widest mb-1" style={{ color: "#475569", fontWeight: 700 }}>
                  · Your full legal name (this is your signature) *
                </div>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  disabled={!scrolledToEnd}
                  placeholder="e.g. Jordan A. Rivera"
                  className="w-full rounded px-3 py-2 text-[14px] disabled:opacity-50"
                  style={{
                    border: "2px solid #0F1B2D", background: scrolledToEnd ? "white" : "#F1F5F9",
                    fontFamily: "'Caveat', cursive, system-ui", fontSize: 20,
                  }}
                  data-testid="nda-name-input"
                />
              </label>

              <label className="flex items-start gap-2 text-[12.5px] mb-4 cursor-pointer" style={{ color: "#0F1B2D" }}>
                <input
                  type="checkbox"
                  checked={agreed}
                  disabled={!scrolledToEnd}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5"
                  data-testid="nda-agree-checkbox"
                />
                <span>
                  I have read and agree to the terms of this Non-Disclosure Agreement. I understand
                  typing my name above and clicking &quot;Sign &amp; Enter&quot; creates a legally binding
                  electronic signature ({user?.email}).
                </span>
              </label>

              {error && (
                <div className="p-2.5 rounded mb-3 text-[12px] font-mono-rm"
                     style={{ background: "#FEF2F2", border: "1.5px solid #FECACA", color: "#B91C1C" }}
                     data-testid="nda-error">
                  {error}
                </div>
              )}

              <button
                onClick={sign}
                disabled={busy || !scrolledToEnd || !agreed || fullName.trim().length < 2}
                className="rm-btn rm-btn-primary w-full py-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="nda-sign-btn"
              >
                {busy ? <Loader size={15} className="animate-spin" /> : <FileSignature size={15} />}
                {busy ? "Signing…" : "Sign & Enter Resmo"}
              </button>
            </>
          )}
        </div>

        <p className="text-center font-mono-rm text-[10px] uppercase tracking-widest mt-4" style={{ color: "#94A3B8" }}>
          Private beta · Your signature is timestamped and stored securely
        </p>
      </div>
    </div>
  );
}
